import { useState, useRef, useEffect, useCallback } from "react";
import { Upload, Image as ImageIcon, X, Loader2, Maximize, AlignCenter, AlignLeft, AlignRight, Minimize, Square, Library } from "lucide-react";
import { imageService } from "@/lib/services/supabaseImageService";
import { apiService } from "@/lib/api-functions/api-service";
import { LoadingSpinner } from "@/components/loaders/loading-spinner";

export type ImageSize = 'small' | 'medium' | 'large';
export type ImageAlignment = 'left' | 'center' | 'right';

interface ImageBlockProps {
  id: string;
  src?: string;
  alt?: string;
  size?: ImageSize;
  alignment?: ImageAlignment;
  gameId?: string;
  onImageChange: (id: string, src: string, alt: string, size?: ImageSize, alignment?: ImageAlignment) => void;
  onDelete: (id: string) => void;
}

interface ImageData {
  name: string;
  path: string;
  url: string;
  size: number;
  createdAt: string;
  updatedAt: string;
  mimetype: string;
}

export const ImageBlock = ({ 
  id, 
  src, 
  alt = "", 
  size = 'medium', 
  alignment = 'center', 
  gameId,
  onImageChange, 
  onDelete 
}: ImageBlockProps) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [currentSize, setCurrentSize] = useState<ImageSize>(size);
  const [currentAlignment, setCurrentAlignment] = useState<ImageAlignment>(alignment);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerMode, setPickerMode] = useState<"upload" | "library">("upload");
  const [libraryImages, setLibraryImages] = useState<ImageData[]>([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Debug log when component mounts or src changes
  useEffect(() => {
    console.log('ImageBlock render:', { id, src, alt, size, alignment, hasSrc: !!src });
  }, [id, src, alt, size, alignment]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files[0] && files[0].type.startsWith('image/')) {
      handleFileUpload(files[0]);
    } else if (files[0]) {
      setUploadError('Please select an image file (PNG, JPG, etc.)');
    }
  };

  const handleFileUpload = async (file: File) => {
    setUploading(true);
    setUploadError(null);
    
    // Check if gameId is available
    if (!gameId) {
      setUploadError('Game ID is required for image upload');
      setUploading(false);
      return;
    }
    
    try {
      // Upload via API with gameId
      const result = await imageService.uploadImage(file, gameId);
      
      if (result.success && result.url) {   
        // Store the image URL along with metadata for tracking
        onImageChange(id, result.url, file.name, currentSize, currentAlignment);
        
        // Log the image ID for debugging
        if (result.id) {
          console.log('Image uploaded successfully with ID:', result.id);
        }
      } else {
        setUploadError(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        handleFileUpload(file);
      } else {
        setUploadError('Please select an image file (PNG, JPG, etc.)');
      }
    }
  };

  const cycleSizeOption = () => {
    const sizes: ImageSize[] = ['small', 'medium', 'large'];
    const currentIndex = sizes.indexOf(currentSize);
    const nextIndex = (currentIndex + 1) % sizes.length;
    const newSize = sizes[nextIndex];
    
    setCurrentSize(newSize);
    if (src) {
      onImageChange(id, src, alt, newSize, currentAlignment);
    }
  };

  const cycleAlignmentOption = () => {
    const alignments: ImageAlignment[] = ['left', 'center', 'right'];
    const currentIndex = alignments.indexOf(currentAlignment);
    const nextIndex = (currentIndex + 1) % alignments.length;
    const newAlignment = alignments[nextIndex];
    
    setCurrentAlignment(newAlignment);
    if (src) {
      onImageChange(id, src, alt, currentSize, newAlignment);
    }
  };

  const getSizeClass = (size: ImageSize) => {
    switch (size) {
      case 'small': return 'max-w-sm';
      case 'medium': return 'max-w-2xl';
      case 'large': return 'max-w-4xl';
      default: return 'max-w-2xl';
    }
  };

  const getAlignmentClass = (alignment: ImageAlignment) => {
    switch (alignment) {
      case 'left': return 'mr-auto';
      case 'center': return 'mx-auto';
      case 'right': return 'ml-auto';
      default: return 'mx-auto';
    }
  };

  const getAlignmentIcon = (alignment: ImageAlignment) => {
    switch (alignment) {
      case 'left': return <AlignLeft size={16} />;
      case 'center': return <AlignCenter size={16} />;
      case 'right': return <AlignRight size={16} />;
      default: return <AlignCenter size={16} />;
    }
  };

  const getSizeIcon = (size: ImageSize) => {
    switch (size) {
      case 'small': return <Minimize size={16} />;
      case 'medium': return <Square size={16} />;
      case 'large': return <Maximize size={16} />;
      default: return <Square size={16} />;
    }
  };

  const fetchLibraryImages = useCallback(async () => {
    if (!gameId) return;
    
    setLoadingLibrary(true);
    try {
      const response = await apiService.listImages(gameId);
      if (response.success && response.data) {
        setLibraryImages(response.data.images);
      }
    } catch (error) {
      console.error('Error fetching library images:', error);
    } finally {
      setLoadingLibrary(false);
    }
  }, [gameId]);

  useEffect(() => {
    if (showPicker && pickerMode === 'library' && gameId) {
      fetchLibraryImages();
    }
  }, [showPicker, pickerMode, gameId, fetchLibraryImages]);

  const handleLibrarySelect = (imageUrl: string, imageName: string) => {
    console.log('ImageBlock - Library select:', { id, imageUrl, imageName, currentSize, currentAlignment });
    onImageChange(id, imageUrl, imageName, currentSize, currentAlignment);
    setShowPicker(false);
  };

  const openPicker = () => {
    setShowPicker(true);
    setUploadError(null);
  };

  if (src) {
    return (
      <div className="group relative">
        {/* Top-right controls */}
        <div className="flex gap-2 absolute top-2 right-2 z-10">
          <button
            onClick={() => onDelete(id)}
            className="p-1 bg-destructive text-destructive-foreground rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          >
            <X size={16} />
          </button>
        </div>

        {/* Left-side controls */}
        <div className="flex flex-col gap-2 absolute left-2 top-2 z-10">
          <button
            onClick={cycleSizeOption}
            className="p-1 bg-background/80 backdrop-blur-sm text-foreground rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 border border-border"
            title={`Size: ${currentSize} (click to cycle)`}
          >
            {getSizeIcon(currentSize)}
          </button>
          <button
            onClick={cycleAlignmentOption}
            className="p-1 bg-background/80 backdrop-blur-sm text-foreground rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 border border-border"
            title={`Alignment: ${currentAlignment} (click to cycle)`}
          >
            {getAlignmentIcon(currentAlignment)}
          </button>
          <button
            onClick={openPicker}
            className="p-1 bg-background/80 backdrop-blur-sm text-foreground rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 border border-border"
            title="Change image"
          >
            <Upload size={16} />
          </button>
        </div>
        
        <div className="w-full flex">
          <img
            src={src}
            alt={alt}
            className={`${getSizeClass(currentSize)} ${getAlignmentClass(currentAlignment)} rounded-lg border border-editor-command-border`}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 ${
          uploading 
            ? 'border-editor-command-border cursor-not-allowed opacity-50' 
            : dragActive 
              ? 'border-primary bg-editor-focus cursor-pointer' 
              : 'border-editor-command-border hover:border-editor-handle cursor-pointer'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !uploading && openPicker()}
      >
        <div className="flex flex-col items-center gap-3">
          <div className="p-3 bg-editor-hover rounded-full">
            {uploading ? (
              <Loader2 className="text-editor-handle animate-spin" size={24} />
            ) : (
              <ImageIcon className="text-editor-handle" size={24} />
            )}
          </div>
          <div>
            <p className="text-editor-text font-medium">
              {uploading ? 'Uploading image...' : 'Add an image'}
            </p>
            <p className="text-editor-text-placeholder text-sm mt-1">
              {uploading ? 'Please wait' : 'Click to upload or choose from library'}
            </p>
          </div>
        </div>
      </div>
      
      {uploadError && (
        <div className="text-red-500 text-sm p-3 bg-red-50 border border-red-200 rounded">
          {uploadError}
        </div>
      )}

      {/* Image picker modal */}
      {showPicker && (
        <div className="border border-editor-command-border rounded-lg p-4 bg-editor-bg shadow-lg">
          {/* Picker header */}
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-editor-command-border">
            <div className="flex gap-2">
              <button
                onClick={() => setPickerMode('upload')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  pickerMode === 'upload'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-editor-text hover:text-editor-text hover:bg-editor-hover'
                }`}
              >
                <Upload size={14} className="inline mr-1" />
                Upload
              </button>
              <button
                onClick={() => setPickerMode('library')}
                disabled={!gameId}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  pickerMode === 'library'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-editor-text hover:text-editor-text hover:bg-editor-hover'
                } ${!gameId ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Library size={14} className="inline mr-1" />
                Library
              </button>
            </div>
            <button
              onClick={() => setShowPicker(false)}
              className="p-1 text-editor-text-placeholder hover:text-editor-text"
            >
              <X size={16} />
            </button>
          </div>

          {/* Picker content */}
          {pickerMode === 'upload' ? (
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                accept="image/*"
                className="hidden"
                id="image-upload-picker"
                disabled={uploading}
              />
              <label
                htmlFor="image-upload-picker"
                className={`w-full h-24 border-2 border-dashed rounded-lg flex items-center justify-center transition-colors cursor-pointer ${
                  uploading
                    ? 'border-editor-command-border cursor-wait opacity-50'
                    : dragActive
                      ? 'border-primary bg-editor-focus'
                      : 'border-editor-command-border hover:border-primary'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="text-center">
                  {uploading ? (
                    <Loader2 size={24} className="mx-auto text-editor-handle mb-1 animate-spin" />
                  ) : (
                    <Upload size={24} className="mx-auto text-editor-handle mb-1" />
                  )}
                  <p className="text-sm text-editor-text">
                    {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                  </p>
                </div>
              </label>
              {uploadError && (
                <div className="text-red-500 text-sm p-2 bg-red-50 border border-red-200 rounded">
                  {uploadError}
                </div>
              )}
            </div>
          ) : (
            <div>
              {loadingLibrary ? (
                <div className="flex items-center justify-center h-24">
                  <LoadingSpinner className="w-6 h-6" />
                </div>
              ) : libraryImages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-24 text-center">
                  <Library className="w-8 h-8 text-editor-text-placeholder mb-2" />
                  <p className="text-sm text-editor-text-placeholder">No images in library</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                  {libraryImages.map((image) => (
                    <button
                      key={image.path}
                      onClick={() => handleLibrarySelect(image.url, image.name)}
                      className="relative aspect-square rounded-md overflow-hidden border-2 border-editor-command-border hover:border-primary transition-all"
                      title={image.name}
                    >
                      <img
                        src={image.url}
                        alt={image.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23ddd" width="100" height="100"/></svg>';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}; 